import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import RefreshCwIcon from '../../shared/Icons/RefreshCwIcon';
import SeriesDateSettingsAlert from './SeriesDateSettingsAlert';
import SeriesDateResetModal from '../../shared/components/SeriesDateResetModal';
import "../styles/eventSeriesBannerStyles.css";

const DAY_NAMES = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const DAY_NAMES_LONG = ["Sundays", "Mondays", "Tuesdays", "Wednesdays", "Thursdays", "Fridays", "Saturdays"];

const formatRange = (startDate, endDate) => {
  if (!startDate || !endDate) return null;
  const start = new Date(`${startDate}T00:00:00`).toLocaleDateString("en-US", { month: "short", day: "numeric" });
  const end   = new Date(`${endDate}T00:00:00`).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  return `${start} to ${end}`;
};

const formatFrequency = (daysOfWeek) => {
  const days = daysOfWeek || [];
  if (days.length === 0) return "";
  if (days.length === 1) return `Every ${DAY_NAMES_LONG[days[0]]}`;
  return `Every ${days.map(n => DAY_NAMES[n]).join("/")}`;
};

// The page holds the whole date in its own state and autosaves it back, so a
// reset reloads it; otherwise the next autosave would undo the reset.
const reloadPage = () => window.location.reload();

const EventSeriesBanner = ({ series, teamId, csrfToken, calendarEventId, startsAt, onDateReset = reloadPage }) => {
  const [ownValues, setOwnValues] = useState(series.own_values);
  useEffect(() => { setOwnValues(series.own_values); }, [series.own_values]);
  const [resetKeys, setResetKeys] = useState(null);
  const canReset = Boolean(calendarEventId) && series.can_update === true;
  const frequency = formatFrequency(series.days_of_week);
  const range     = formatRange(series.start_date, series.end_date);
  const showUrl   = `/teams/${teamId}/event_series/${series.id}`;

  return (
    <>
      <a href={showUrl} className="d-flex align-items-center my-3 p-3 rounded event-series-banner">
        <div className="d-flex align-items-center justify-content-center rounded event-series-banner-icon">
          <RefreshCwIcon width="14" height="14" />
        </div>
        <div className="flex-grow-1 event-series-banner-body">
          <div className="event-series-banner-title">
            Part of <strong>{series.name}</strong> series
          </div>
          <div className="event-series-banner-meta">
            <strong>{series.events_count} events</strong>
            {series.grouping ? (
              range && <><span className="event-series-banner-dot">&middot;</span>{range}</>
            ) : frequency ? <><span className="event-series-banner-dot">&middot;</span>{frequency} from {range}</> : null}
            {series.position ? (
              <>
                <span className="event-series-banner-dot">&middot;</span>
                This is event {series.position} of {series.events_count}
              </>
            ) : null}
          </div>
        </div>
        <span className="event-series-banner-cta">
          View series <i className="fas fa-arrow-right event-series-banner-arrow" />
        </span>
      </a>
      {Array.isArray(ownValues) && (
        <SeriesDateSettingsAlert
          series={series}
          ownValues={ownValues}
          onReset={canReset ? (keys) => setResetKeys(keys) : undefined}
          onResetAll={canReset ? () => setResetKeys([]) : undefined}
        />
      )}
      {resetKeys && (
        <SeriesDateResetModal
          teamId={teamId}
          seriesId={series.id}
          seriesName={series.name}
          calendarEventId={calendarEventId}
          startsAt={startsAt}
          keys={resetKeys}
          timeZone={series.venue_time_zone}
          currency={series.base_currency_symbol}
          csrfToken={csrfToken}
          onClose={() => setResetKeys(null)}
          onReset={(result) => {
            setResetKeys(null);
            // A queued reset hasn't run, so the date still holds its own values,
            // and a reload would only wipe the note saying it's on the way.
            if (result.queued_count) return;
            setOwnValues(result.own_values);
            onDateReset(result);
          }}
        />
      )}
    </>
  );
};

EventSeriesBanner.propTypes = {
  series: PropTypes.object.isRequired,
  teamId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  // Only the reset posts, so only a viewer who can reset needs it.
  csrfToken: (props, propName, componentName) => {
    const value = props[propName];
    if (value !== undefined && value !== null && typeof value !== 'string') {
      return new Error(`Invalid prop \`${propName}\` supplied to \`${componentName}\`; expected a string.`);
    }
    if (!value && Boolean(props.calendarEventId) && props.series && props.series.can_update === true) {
      return new Error(`The prop \`${propName}\` is required by \`${componentName}\` when the viewer can reset the date.`);
    }
    return null;
  },
  calendarEventId: PropTypes.number,
  startsAt: PropTypes.string,
  onDateReset: PropTypes.func,
};

export default EventSeriesBanner;
